import { useMemo, type ReactNode } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { Currency, PortfolioAllocationResponse } from "@richrich/contracts";
import { ChartFrame, type LegendItem } from "./ChartFrame";
import { categoricalColor, paletteFor } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { decimalToNumber, formatMoney, formatPercent } from "../lib/format";

export interface AllocationDonutProps {
  data: PortfolioAllocationResponse;
  theme?: Theme;
  height?: number;
  loading?: boolean;
  title?: ReactNode;
  className?: string;
  /** Max distinct stock slices before folding the tail into "Other". */
  maxSlices?: number;
}

interface Slice {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface TooltipInjected {
  active?: boolean;
  payload?: Array<{ payload?: Slice }>;
}

/**
 * Stocks-vs-cash + per-symbol allocation donut (ARCHITECTURE §8, recharts). Uses
 * the fixed categorical order for symbols (never cycled); the tail beyond
 * `maxSlices` folds into a single "Other" slice and cash gets a reserved muted
 * slot, so identity stays stable as holdings change. Total sits in the center.
 */
export function AllocationDonut({
  data,
  theme,
  height = 260,
  loading = false,
  title = "Allocation",
  className,
  maxSlices = 7,
}: AllocationDonutProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);
  const currency = data.baseCurrency as Currency;

  const { slices, total } = useMemo(() => {
    const cash = decimalToNumber(data.cashValue);
    const symbols = data.bySymbol
      .map((s) => ({ symbol: s.symbol as string, name: s.name, value: decimalToNumber(s.value) }))
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);

    const head = symbols.slice(0, maxSlices);
    const tail = symbols.slice(maxSlices);

    const result: Slice[] = head.map((s, i) => ({
      key: s.symbol,
      label: s.name || s.symbol,
      value: s.value,
      color: categoricalColor(palette, i),
    }));

    if (tail.length > 0) {
      const otherValue = tail.reduce((sum, s) => sum + s.value, 0);
      result.push({
        key: "__other__",
        label: `Other (${tail.length})`,
        value: otherValue,
        color: palette.muted,
      });
    }
    if (cash > 0) {
      result.push({ key: "__cash__", label: "Cash", value: cash, color: palette.axis });
    }

    const sum = result.reduce((acc, s) => acc + s.value, 0);
    return { slices: result, total: sum };
  }, [data.bySymbol, data.cashValue, maxSlices, palette]);

  const legend: LegendItem[] = slices.map((s) => ({
    label: `${s.label} · ${formatPercent(total > 0 ? s.value / total : 0, 1)}`,
    color: s.color,
  }));

  const empty = slices.length === 0 || total <= 0;

  return (
    <ChartFrame
      title={title}
      legend={legend}
      height={height}
      loading={loading}
      empty={empty}
      className={className}
    >
      <div className="relative" style={{ height }}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="62%"
              outerRadius="88%"
              paddingAngle={1.5}
              stroke={palette.surface}
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((s) => (
                <Cell key={s.key} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              content={<AllocationTooltip currency={currency} total={total} />}
              wrapperStyle={{ zIndex: 20 }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center">
          <span className="text-xs font-medium text-muted">Total</span>
          <span className="text-lg font-semibold tabular-nums text-ink">
            {formatMoney(String(Math.round(total)), currency)}
          </span>
        </div>
      </div>
    </ChartFrame>
  );
}

function AllocationTooltip({
  active,
  payload,
  currency,
  total,
}: TooltipInjected & { currency: Currency; total: number }): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  const slice = payload[0]?.payload;
  if (!slice) return null;
  return (
    <div className="rounded-lg border border-hairline bg-elevated px-3 py-2 text-xs shadow-md">
      <div className="mb-1 flex items-center gap-1.5 font-medium text-ink">
        <span
          className="inline-block size-2.5 rounded-[3px]"
          style={{ backgroundColor: slice.color }}
        />
        {slice.label}
      </div>
      <div className="flex items-center justify-between gap-6 tabular-nums">
        <span className="text-muted">Value</span>
        <span className="font-medium text-ink">
          {formatMoney(String(Math.round(slice.value)), currency)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-6 tabular-nums">
        <span className="text-muted">Weight</span>
        <span className="font-medium text-ink">
          {formatPercent(total > 0 ? slice.value / total : 0, 1)}
        </span>
      </div>
    </div>
  );
}
